// src/features/stock/pages/StockDetailPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Package,
  Calendar,
  AlertTriangle,
  History,
  Printer
} from 'lucide-react';
import { format } from 'date-fns';
import stockApi from '../services/stockApi';
import { useStockMovement } from '../hooks/useStockMovement';
import Button from '../../../shared/components/Button';
import Card from '../../../shared/components/Card';
import Spinner from '../../../shared/components/Spinner';
import StockMovementTable from '../components/StockMovementTable';

const StockDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const { movements, loading: movementsLoading, getItemMovements } = useStockMovement();

  useEffect(() => {
    fetchItemDetails();
  }, [id]);

  const fetchItemDetails = async () => {
    setLoading(true);
    try {
      const response = await stockApi.getStockItem(id);
      setItem(response.data.data);
      await getItemMovements(id);
    } catch (error) {
      console.error('Failed to fetch item details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => navigate(`/stock/${id}/edit`);

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await stockApi.deleteStockItem(id);
        navigate('/stock');
      } catch (error) {
        console.error('Failed to delete item:', error);
      }
    }
  };

  const handlePrint = () => window.print();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!item) {
    return (
      <div className="text-center py-12">
        <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900">Item not found</h3>
      </div>
    );
  }

  const isLowStock = item.total_available <= item.reorder_level;
  const isOutOfStock = item.stock_status === 'out_of_stock';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/stock')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {item.display_name}
            </h1>
            <p className="text-gray-600">Code: {item.code}</p>
          </div>
        </div>

        <div className="flex space-x-3">
          <Button variant="secondary" icon={Printer} onClick={handlePrint}>
            Print
          </Button>
          <Button variant="secondary" icon={Edit} onClick={handleEdit}>
            Edit
          </Button>
          <Button variant="danger" icon={Trash2} onClick={handleDelete}>
            Delete
          </Button>
        </div>
      </div>

      {/* Stock Alerts */}
      {(isLowStock || isOutOfStock) && (
        <div className="space-y-2">
          {isOutOfStock && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              <p className="text-red-700 font-medium">
                This item is currently OUT OF STOCK
              </p>
            </div>
          )}

          {isLowStock && !isOutOfStock && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              <p className="text-yellow-700">
                <span className="font-medium">Low Stock Alert:</span>{' '}
                Available ({item.total_available} {item.unit}) is below reorder
                level ({item.reorder_level} {item.unit})
              </p>
            </div>
          )}
        </div>
      )}

      {/* Basic Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="md:col-span-2">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Medicine Information
          </h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Category</p>
              <p className="font-medium">{item.category?.name}</p>
            </div>

            <div>
              <p className="text-sm text-gray-500">Form</p>
              <p className="font-medium">{item.form}</p>
            </div>

            <div>
              <p className="text-sm text-gray-500">Strength</p>
              <p className="font-medium">{item.strength}</p>
            </div>

            <div>
              <p className="text-sm text-gray-500">Manufacturer</p>
              <p className="font-medium">{item.manufacturer}</p>
            </div>

            <div>
              <p className="text-sm text-gray-500">Unit</p>
              <p className="font-medium">{item.unit}</p>
            </div>

            <div>
              <p className="text-sm text-gray-500">Available Stock</p>
              <p className="font-medium text-2xl text-primary-600">
                {item.total_available} {item.unit}
              </p>
            </div>

            <div>
              <p className="text-sm text-gray-500">Minimum Stock</p>
              <p className="font-medium">
                {item.minimum_stock} {item.unit}
              </p>
            </div>

            <div>
              <p className="text-sm text-gray-500">Maximum Stock</p>
              <p className="font-medium">
                {item.maximum_stock} {item.unit}
              </p>
            </div>
          </div>

          {item.description && (
            <div className="mt-4">
              <p className="text-sm text-gray-500">Description</p>
              <p className="text-gray-700">{item.description}</p>
            </div>
          )}

          {item.instructions && (
            <div className="mt-4">
              <p className="text-sm text-gray-500">Instructions</p>
              <p className="text-gray-700">{item.instructions}</p>
            </div>
          )}
        </Card>

        {/* Storage & Compliance */}
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Storage & Compliance
          </h3>

          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-500">Storage Conditions</p>
              <p className="font-medium">{item.storage_conditions}</p>
            </div>

            <div>
              <p className="text-sm text-gray-500">Prescription Required</p>
              <p className="font-medium">
                {item.requires_prescription ? 'Yes' : 'No'}
              </p>
            </div>

            <div>
              <p className="text-sm text-gray-500">Controlled Drug</p>
              <p className="font-medium">
                {item.is_controlled ? 'Yes' : 'No'}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Stock Movements */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <History className="h-5 w-5 text-gray-500" />
            <h3 className="text-lg font-semibold text-gray-900">
              Stock Movement History
            </h3>
          </div>

          <Button
            variant="primary"
            size="sm"
            onClick={() => navigate(`/stock/${id}/movements/new`)}
          >
            Record Movement
          </Button>
        </div>

        <StockMovementTable
          movements={movements}
          loading={movementsLoading}
          onView={(movement) => console.log('View movement', movement)}
        />
      </Card>
    </div>
  );
};

export default StockDetailPage;